import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { FEEDBACK_COUNT_EXPIRY_TIME } from './application-score.constants';

export const useApplicationScoreStore = defineStore(
  'applicationScore',
  () => {
    const scores = ref(new Map<number, ATSApplicationScoringProScoring>());
    // feedback counts must be serializable to be persisted
    const feedbackCounts = ref<
      Record<
        number,
        {
          currentCount: number;
          threshold: number;
          timestamp: number;
          dismissed: boolean;
        }
      >
    >({});

    function setScoreDetail(proScore: ATSApplicationScoringProScoring) {
      if (!proScore.jobApplicationId) {
        throw new Error('jobApplicationId is required');
      }

      scores.value.set(proScore.jobApplicationId, proScore);
    }

    function getScoreDetail(applicationId: number) {
      return scores.value.get(applicationId);
    }

    function cleanupExpiredFeedbackCounts() {
      const now = Date.now();

      for (const [jobId, value] of Object.entries(feedbackCounts.value)) {
        if (now - value.timestamp > FEEDBACK_COUNT_EXPIRY_TIME) {
          delete feedbackCounts.value[Number(jobId)];
        }
      }
    }

    function setFeedbackCount(jobId: number, data: { currentCount: number; threshold: number }) {
      cleanupExpiredFeedbackCounts();

      feedbackCounts.value[jobId] = {
        ...data,
        timestamp: Date.now(),
        dismissed: false,
      };
    }

    function getFeedbackCountData(jobId: number) {
      return feedbackCounts.value[jobId];
    }

    function dismissFeedbackBanner(jobId: number) {
      if (feedbackCounts.value[jobId]) {
        feedbackCounts.value[jobId].dismissed = true;
      }
    }

    // alphabetized list
    return {
      dismissFeedbackBanner,
      getFeedbackCountData,
      getScoreDetail,
      setFeedbackCount,
      setScoreDetail,
      // expose property for persistence
      feedbackCounts,
    };
  },
  {
    persist: true,
  },
);
